// Where structure edits put things, as the Mac decides (LibraryModel+Editing: `newFragment`'s placement,
// `moveSelection` for the Structure menu's keys, the binder's drops), in the core's `Placement` terms. Pure: the tree
// in, a placement out, so the unit tests run them. An `{ inside, index }` counts only the siblings that aren't moving,
// as `ProjectEditor.move` does.
import { JournalPosts, journalPostsInTree, postContaining, type JournalPostRef, type MetadataValue, type Placement, type TreeNode } from '@writing-suite/core';

export interface Located {
  node: TreeNode;
  parent: TreeNode | null;
  siblings: TreeNode[];
  index: number;
}

export function locate(tree: TreeNode[], id: string, parent: TreeNode | null = null): Located | null {
  const siblings = parent ? parent.children : tree;
  for (let index = 0; index < siblings.length; index++) {
    const node = siblings[index];
    if (node.id === id) return { node, parent, siblings, index };
    const inner = locate(tree, id, node);
    if (inner) return inner;
  }
  return null;
}

/** A folder, or anything with children: drops can go into it. */
export const isFolderish = (n: TreeNode) => n.isFolder || n.children.length > 0;

/** Whether `id` is `ancestor` or under it. */
export function isWithin(tree: TreeNode[], id: string, ancestor: string): boolean {
  const start = locate(tree, ancestor)?.node;
  if (!start) return false;
  const walk = (n: TreeNode): boolean => n.id === id || n.children.some(walk);
  return walk(start);
}

export type DropZone = 'before' | 'after' | 'into';

/** The zone of a row a pointer is over (a fraction of its height from the top): a folder takes its middle half. */
export function dropZone(fraction: number, folder: boolean): DropZone {
  if (folder) return fraction < 0.25 ? 'before' : fraction > 0.75 ? 'after' : 'into';
  return fraction < 0.5 ? 'before' : 'after';
}

/** Where dropping `moving` before, after or into `target` puts them; null when it can't go there (onto itself or
 *  into its own subtree). */
export function dropPlacement(tree: TreeNode[], moving: string[], targetId: string, zone: DropZone): Placement | null {
  if (moving.some((m) => isWithin(tree, targetId, m))) return null;
  const at = locate(tree, targetId);
  if (!at) return null;
  if (zone === 'into') return { inside: at.node.id };
  const staying = at.siblings.filter((n) => !moving.includes(n.id));
  const i = staying.findIndex((n) => n.id === targetId);
  return { inside: at.parent?.id ?? null, index: zone === 'before' ? i : i + 1 };
}

export type KeyMove = 'up' | 'down' | 'into' | 'outOf';

/** `moveSelection`: up and down among siblings, into the one above (at its end), out of its folder (just after it). */
export function keyMovePlacement(tree: TreeNode[], id: string, direction: KeyMove): Placement | null {
  const at = locate(tree, id);
  if (!at) return null;
  const { parent, siblings, index } = at;
  switch (direction) {
    case 'up':
      return index > 0 ? { inside: parent?.id ?? null, index: index - 1 } : null;
    case 'down':
      return index + 1 < siblings.length ? { inside: parent?.id ?? null, index: index + 1 } : null;
    case 'into':
      return index > 0 ? { inside: siblings[index - 1].id } : null;
    case 'outOf': {
      if (!parent) return null;
      const up = locate(tree, parent.id)!;
      return { inside: up.parent?.id ?? null, index: up.index + 1 };
    }
  }
}

/** `newFragment`'s placement: a folder goes after the selection; a scene after a document or inside a folder (at its
 *  end); a journal's post first in its folder; with nothing selected, at the end of the top level. */
export function newPlacement(tree: TreeNode[], selectedId: string | null, o: { folder: boolean; post: boolean }): Placement {
  const at = selectedId ? locate(tree, selectedId) : null;
  if (o.post) {
    // The chosen folder, else the one holding the chosen post: never inside a post, which would make it a section.
    const post = at ? postContaining(at.node.id, journalPostsInTree(tree)) : null;
    const folder = post ? post.parentId : at ? (at.node.isFolder ? at.node.id : (at.parent?.id ?? null)) : null;
    return folder === null ? { first: true } : { inside: folder, index: 0 };
  }
  if (!at) return { inside: null };
  return o.folder || !at.node.isFolder ? { after: at.node.id } : { inside: at.node.id };
}

/** The ids between two rows of a visible list, both included (a ⇧-click's range). */
export function rangeBetween(rows: string[], a: string, b: string): string[] {
  const i = rows.indexOf(a), j = rows.indexOf(b);
  if (i < 0 || j < 0) return [b];
  return rows.slice(Math.min(i, j), Math.max(i, j) + 1);
}

/** Every node's fragment under these nodes (each node's own first), once each. */
export function fragmentsOf(tree: TreeNode[], nodeIds: string[]): string[] {
  const out: string[] = [];
  for (const id of nodeIds) {
    const n = locate(tree, id)?.node;
    if (n?.fragmentId && !out.includes(n.fragmentId)) out.push(n.fragmentId);
  }
  return out;
}

/** The nearest word target at or above a node (`chapterTarget`): the node whose `word-target` it is, and the target. */
export function nearestTarget(
  tree: TreeNode[],
  nodeId: string | null,
  metadata: (fragmentId: string) => Record<string, MetadataValue> | undefined,
): { node: TreeNode; target: number } | null {
  if (!nodeId) return null;
  const path: TreeNode[] = [];
  const find = (nodes: TreeNode[]): boolean => {
    for (const n of nodes) {
      path.push(n);
      if (n.id === nodeId || find(n.children)) return true;
      path.pop();
    }
    return false;
  };
  if (!find(tree)) return null;
  for (const n of [...path].reverse()) {
    const v = n.fragmentId ? metadata(n.fragmentId)?.['word-target'] : undefined;
    const target = v?.type === 'number' ? v.value : v?.type === 'text' ? Number(v.value.replace(/[^0-9]/g, '')) : NaN;
    if (Number.isFinite(target) && target >= 1) return { node: n, target: Math.round(target) };
  }
  return null;
}

/** The first node showing a fragment (under `nodes`, in reading order). */
export function nodeOfFragment(nodes: TreeNode[], fragmentId: string): TreeNode | null {
  for (const n of nodes) {
    if (n.fragmentId === fragmentId) return n;
    const inner = nodeOfFragment(n.children, fragmentId);
    if (inner) return inner;
  }
  return null;
}

/** A typed word target (`WordTargetInput.value`): its first nine digits; empty or zero removes it. */
export function wordTargetInput(text: string): MetadataValue | null {
  const digits = text.replace(/[^0-9]/g, '').replace(/^0+/, '').slice(0, 9);
  const n = Number(digits);
  return digits && n > 0 ? { type: 'number', value: n } : null;
}

/** A typed number (`FieldEditor`'s number case): digits, a point and a minus sign; anything unreadable clears it. */
export function numberInput(text: string): MetadataValue | null {
  const kept = text.replace(/[^0-9.-]/g, '');
  if (!kept) return null;
  const n = Number(kept);
  return Number.isFinite(n) && Math.abs(n) < 1e15 ? { type: 'number', value: n } : null;
}

// MARK: Journal posts and sections

/** The folder a placement puts things in (null: the top level). */
export function placementParent(tree: TreeNode[], placement: Placement): string | null {
  if ('after' in placement) return locate(tree, placement.after)?.parent?.id ?? null;
  if ('inside' in placement) return placement.inside;
  return null;
}

/** The tree with these nodes (and what's under them) moved into `parentId`, at the end: enough to ask which items would
 *  be posts (`Arrangement.moving`). */
export function treeMoving(tree: TreeNode[], nodeIds: string[], parentId: string | null): TreeNode[] {
  const moving = new Set(nodeIds);
  const taken: TreeNode[] = [];
  const strip = (nodes: TreeNode[]): TreeNode[] => nodes.flatMap((n) => {
    if (moving.has(n.id)) { taken.push(n); return []; }
    return [{ ...n, children: strip(n.children) }];
  });
  const rest = strip(tree);
  if (parentId === null) return [...rest, ...taken];
  const put = (nodes: TreeNode[]): TreeNode[] => nodes.map((n) => (n.id === parentId ? { ...n, children: [...n.children, ...taken] } : { ...n, children: put(n.children) }));
  return put(rest);
}

/** The tree with these nodes made folders or documents. */
export function treeConverting(tree: TreeNode[], nodeIds: string[], toFolder: boolean): TreeNode[] {
  const ids = new Set(nodeIds);
  const walk = (nodes: TreeNode[]): TreeNode[] => nodes.map((n) => ({ ...n, isFolder: ids.has(n.id) ? toFolder : n.isFolder, children: walk(n.children) }));
  return walk(tree);
}

/** `JournalPosts.demoted` over view trees: posts `after` makes sections, each with the post that would hold it. */
export function demotedInTree(before: TreeNode[], after: TreeNode[]): { post: JournalPostRef; holder: JournalPostRef }[] {
  const now = journalPostsInTree(after);
  return journalPostsInTree(before).flatMap((post) => {
    if (now.some((p) => p.fragmentId === post.fragmentId)) return [];
    const holder = postContaining(post.fragmentId, now);
    return holder ? [{ post, holder }] : [];
  });
}

export interface StructureAsk { title: string; message: string; confirm?: string; onConfirm?: () => void }

const titleIn = (tree: TreeNode[], fragmentId: string) => {
  const walk = (nodes: TreeNode[]): string | null => {
    for (const n of nodes) { if (n.fragmentId === fragmentId) return n.title; const t = walk(n.children); if (t !== null) return t; }
    return null;
  };
  return walk(tree) || 'Untitled';
};

/** What to ask before posts become sections of another (the Mac's `postsBecomeSections`); null when none would, or when
 *  one has a number (the core refuses that with its own words). */
export function postsBecomeSections(tree: TreeNode[], after: TreeNode[], metadataOf: (fragmentId: string) => Record<string, MetadataValue> | undefined):
  { title: string; message: string; confirm: string } | null {
  const demoted = demotedInTree(tree, after);
  if (!demoted.length || demoted.some((d) => JournalPosts.number(metadataOf(d.post.fragmentId)) !== null)) return null;
  const holders = new Set(demoted.map((d) => d.holder.fragmentId));
  const holder = titleIn(tree, demoted[0].holder.fragmentId);
  const message = "A section isn't numbered, scheduled or shared on its own: its text goes out as part of the post.";
  if (demoted.length === 1) return { title: `Make “${titleIn(tree, demoted[0].post.fragmentId)}” a section of “${holder}”?`, message, confirm: 'Make Section' };
  return {
    title: holders.size === 1 ? `Make ${demoted.length} posts sections of “${holder}”?` : `Make ${demoted.length} posts sections of other posts?`,
    message, confirm: 'Make Sections',
  };
}

/** What to ask before posts with sections become folders (their sections become posts of their own). */
export function sectionsBecomePosts(tree: TreeNode[], nodeIds: string[]): { title: string; message: string } | null {
  const was = new Set(journalPostsInTree(tree).map((p) => p.fragmentId));
  const promoted = journalPostsInTree(treeConverting(tree, nodeIds, true)).filter((p) => !was.has(p.fragmentId));
  if (!promoted.length) return null;
  const converted = journalPostsInTree(tree).filter((p) => nodeIds.includes(p.nodeId) && p.sections.length > 0);
  const sections = promoted.length === 1 ? '1 section' : `${promoted.length} sections`;
  if (converted.length === 1) {
    const name = titleIn(tree, converted[0].fragmentId);
    return { title: `Convert “${name}” to a folder?`, message: `“${name}” has ${sections}. As a folder, each becomes a post of its own.` };
  }
  return { title: 'Convert to folders?', message: `${sections.charAt(0).toUpperCase() + sections.slice(1)} would each become a post of its own.` };
}

/** "#141 Coming home · Section 2 of 4" (the Mac's `postPlace`), while the caret is in a post with sections. */
export function postPlace(tree: TreeNode[], fragmentId: string | null, metadataOf: (fragmentId: string) => Record<string, MetadataValue> | undefined): string | null {
  if (!fragmentId) return null;
  const post = postContaining(fragmentId, journalPostsInTree(tree));
  if (!post || !post.sections.length) return null;
  const title = titleIn(tree, post.fragmentId);
  const number = JournalPosts.number(metadataOf(post.fragmentId));
  const name = number !== null ? `#${number} ${title}` : title;
  // Sections with text of their own count; a folder of sections doesn't.
  const sections = post.sections.filter((s) => !locate(tree, s.nodeId)?.node.isFolder);
  const index = sections.findIndex((s) => s.fragmentId === fragmentId);
  return index < 0 ? name : `${name} · Section ${index + 1} of ${sections.length}`;
}

